/*****************************************************
Naam: useDashboardCounts.js
Locatie: src/components/Dashboard/hooks/useDashboardCounts.js
Datum: 2026-02-27
Versie: 3.0.0
******************************************************
Beschrijving:
Custom hook voor dashboard counts (aanvragen, notificaties, DOT, deadlines).

CHANGELOG v3.0.0 (2026-02-27):
- OPTIMALISATIE: 5 aparte /api/load-aanvragen calls → 1 single fetch
- Alle counts worden berekend uit dezelfde dataset
- ~80% minder API calls bij elke refresh

CHANGELOG v2.0.0 (2026-02-25):
- NIEUW: deadlineVerlopenCount voor verlopen offerte-deadlines
******************************************************/

import { useState, useEffect, useCallback } from 'react';
import { isDotVerlopen } from '../utils/dashboardHelpers';

export const useDashboardCounts = () => {
  const [nieuweAanvragenCount, setNieuweAanvragenCount] = useState(0);
  const [totaalAanvragenCount, setTotaalAanvragenCount] = useState(0);
  const [prijzenBinnenCount, setPrijzenBinnenCount] = useState(0);
  const [opdrachtBevestigingCount, setOpdrachtBevestigingCount] = useState(0);
  const [notificationCount, setNotificationCount] = useState(0);
  const [dotVerlopenCount, setDotVerlopenCount] = useState(0);
  const [trackingOverdueCount, setTrackingOverdueCount] = useState(0);
  const [deadlineVerlopenCount, setDeadlineVerlopenCount] = useState(0);
  const [afgeschrevenCount, setAfgeschrevenCount] = useState(0);
  const [klantenCount, setKlantenCount] = useState(0);
  const [vervoerdersCount, setVervoerdersCount] = useState(0);
  const [geoffreerdCount, setGeoffreerdCount] = useState(0);
  const [factuurVervallenCount, setFactuurVervallenCount] = useState(0);

  // v3.0: Single fetch voor alle aanvragen-counts
  const loadAllAanvragenCounts = useCallback(async () => {
    try {
      const response = await fetch('/api/load-aanvragen');
      const result = await response.json();
      if (result.success && result.data) {
        const data = result.data;
        
        // Nieuwe aanvragen
        setTotaalAanvragenCount(data.length);
        setNieuweAanvragenCount(
          data.filter(a => a.Status === 'Prijzen nog aan te vragen' || a.Status === 'Prijzen uitgezet').length
        );
        
        // Prijzen binnen
        setPrijzenBinnenCount(
          data.filter(a => a.Status === 'Prijzen vervoerders binnen').length
        );
        
        // Opdracht bevestiging
        setOpdrachtBevestigingCount(
          data.filter(a => a.Status === 'Opdracht ontvangen' || a.Status === 'Opdracht').length
        );
        
        // Tracking overdue
        const today = new Date(); today.setHours(0, 0, 0, 0);
        setTrackingOverdueCount(
          data.filter(a => {
            if (!a.TrackerID) return false;
            const status = (a.Status || '').toLowerCase();
            if (!status.includes('opdracht') && !status.includes('onderweg') && !status.includes('bevestigd')) return false;
            if (!a.LosDatum) return false;
            const los = new Date(a.LosDatum); los.setHours(0, 0, 0, 0);
            return today > los;
          }).length
        );
        
        // Geoffreerd
        setGeoffreerdCount(
          data.filter(a => a.Status === 'Prijs geoffreerd').length
        );
        
        // Deadline verlopen (exclusief afgeschreven)
        setDeadlineVerlopenCount(
          data.filter(a => a.DeadlineVerlopen === 'J' && !a.GekozenVervoerder && a.Status !== 'Afgeschreven').length
        );
      }
    } catch (error) {
      console.error('Error loading aanvragen counts:', error);
    }
  }, []);

  const loadNotificationCount = useCallback(async () => {
    try {
      const response = await fetch('/api/notifications/unread-count');
      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          setNotificationCount(data.count);
        }
      }
    } catch (err) {
      console.error('Load notification count error:', err);
    }
  }, []);

  const loadDotVerlopenCount = useCallback(async () => {
    try {
      const response = await fetch('/api/load-vervoerders');
      const result = await response.json();
      if (result.success) {
        setVervoerdersCount(result.data.length);
        const count = result.data.filter(v => 
          v.prijzenAanwezig === 'J' && isDotVerlopen(v.dotGeldig, v.dotPeriode)
        ).length;
        setDotVerlopenCount(count);
      }
    } catch (error) {
      console.error('Error loading DOT verlopen count:', error);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadAfgeschrevenCount = useCallback(async () => {
    try {
      const res = await fetch('/api/afgeschreven-aanvragen');
      const result = await res.json();
      if (result.success) {
        setAfgeschrevenCount(result.data ? result.data.length : 0);
      }
    } catch (e) {
      console.error('Error loading afgeschreven count:', e);
    }
  }, []);

  const loadKlantenCount = useCallback(async () => {
    try {
      const res = await fetch('/api/load-klanten');
      const result = await res.json();
      if (result.success) {
        setKlantenCount(result.data ? result.data.length : 0);
      }
    } catch (e) {
      console.error('Error loading klanten count:', e);
    }
  }, []);

  // Tel facturen met vervallen betaaltermijn (Status=Verzonden of Herinnering, VervalDatum < vandaag)
  const loadFactuurVervallenCount = useCallback(async () => {
    try {
      const res = await fetch('/api/facturen');
      const result = await res.json();
      if (result.success && result.facturen) {
        const vandaag = new Date(); vandaag.setHours(0, 0, 0, 0);
        const parseDatum = (str) => {
          if (!str) return null;
          const s = String(str);
          if (/^\d{2}-\d{2}-\d{4}$/.test(s)) {
            const [d, m, y] = s.split('-');
            return new Date(Number(y), Number(m) - 1, Number(d));
          }
          const p = new Date(s);
          return isNaN(p.getTime()) ? null : p;
        };
        const count = result.facturen.filter(f => {
          if (f.Status === 'Betaald' || f.Status === 'Geannuleerd') return false;
          const verval = parseDatum(f.VervalDatum);
          return verval && verval < vandaag;
        }).length;
        setFactuurVervallenCount(count);
      }
    } catch (e) {
      console.error('Error loading factuur vervallen count:', e);
    }
  }, []);

  const refreshAllCounts = useCallback(() => {
    loadAllAanvragenCounts();
    loadDotVerlopenCount();
    loadAfgeschrevenCount();
    loadKlantenCount();
    loadFactuurVervallenCount();
  }, [loadAllAanvragenCounts, loadDotVerlopenCount, loadAfgeschrevenCount, loadKlantenCount, loadFactuurVervallenCount]);

  // Initial load + 30s interval
  useEffect(() => {
    loadAllAanvragenCounts();
    loadDotVerlopenCount();
    loadAfgeschrevenCount();
    loadKlantenCount();
    loadFactuurVervallenCount();
    loadNotificationCount();
    
    const countsInterval = setInterval(refreshAllCounts, 30000);
    const notificationInterval = setInterval(loadNotificationCount, 30000);
    
    return () => {
      clearInterval(countsInterval);
      clearInterval(notificationInterval);
    };
  }, [loadAllAanvragenCounts, loadDotVerlopenCount, loadAfgeschrevenCount, loadKlantenCount, loadFactuurVervallenCount, loadNotificationCount, refreshAllCounts]);

  // Luister naar refresh-opdracht-count event van socket
  useEffect(() => {
    const handleRefreshOpdrachtCount = () => {
      loadAllAanvragenCounts();
    };
    
    window.addEventListener('refresh-opdracht-count', handleRefreshOpdrachtCount);
    
    return () => {
      window.removeEventListener('refresh-opdracht-count', handleRefreshOpdrachtCount);
    };
  }, [loadAllAanvragenCounts]);

  return {
    nieuweAanvragenCount, totaalAanvragenCount,
    prijzenBinnenCount,
    opdrachtBevestigingCount,
    notificationCount,
    dotVerlopenCount,
    trackingOverdueCount,
    deadlineVerlopenCount,
    afgeschrevenCount,
    klantenCount,
    vervoerdersCount,
    geoffreerdCount,
    factuurVervallenCount,
    refreshAllCounts,
    loadNotificationCount,
    loadDeadlineVerlopenCount: loadAllAanvragenCounts // alias voor backward compat
  };
};

export default useDashboardCounts;
